import { Link } from "react-router";
import { usePageMeta } from "../components/Layout.tsx";

// Written for what this app actually does rather than copied from a generator,
// because a policy that describes data handling the app does not do is worse
// than none: it is a claim that happens to be false.
//
// The account itself is a gazar.dev account, so anything about accounts,
// payments and support points at gazar.dev's own policies rather than being
// restated here where the two could drift apart.

const UPDATED = "17 August 2026";

export function Terms() {
  usePageMeta("Terms", "Terms of use for Coding.");
  return (
    <article className="prose-page">
      <h1>Terms</h1>
      <p className="muted">Last updated {UPDATED}.</p>

      <h2>What you are getting</h2>
      <p>
        Coding is a free practice tool. There is no paid tier, nothing is charged, and no payment details
        are collected here. It is provided as-is, with no guarantee that it is available, correct, or that
        practising on it will help you pass anything.
      </p>

      <h2>Your account</h2>
      <p>
        Your account is a gazar.dev account and is governed by{" "}
        <a href="https://gazar.dev/terms">gazar.dev's terms</a>. One person, one account. Keep your
        password to yourself.
      </p>

      <h2>What you may not do</h2>
      <ul>
        <li>Automate attempts, or script the app to inflate a score.</li>
        <li>Pick a handle that impersonates someone else or is designed to offend.</li>
        <li>Attack the service, or use it to attack anything else.</li>
      </ul>
      <p>
        The remedy for any of these is removal from the scoreboard or removal of the account. Nothing here
        is subtle enough to need a warning system.
      </p>

      <h2>The problems and the code</h2>
      <p>
        The problems and their reference solutions are the author's, and are for your own practice. Do not
        republish them as your own. Code you write while solving a problem is yours, and it never leaves
        your browser in the first place.
      </p>

      <h2>Ending it</h2>
      <p>
        You can stop at any time. Ask and the account and everything attached to it will be deleted; see{" "}
        <Link to="/privacy">Privacy</Link>.
      </p>

      <h2>Changes</h2>
      <p>
        These may change. The date at the top says when they last did. Nothing changes retroactively about
        money, because there is none.
      </p>
    </article>
  );
}

export function Privacy() {
  usePageMeta("Privacy", "What Coding stores about you, and what it never does.");
  return (
    <article className="prose-page">
      <h1>Privacy</h1>
      <p className="muted">Last updated {UPDATED}.</p>

      <h2>Your solutions never leave your browser</h2>
      <p>
        Code you write is compiled and run client-side, in a worker destroyed after each run. It is never
        transmitted and never stored. What is stored is the outcome: which problem, how long it took, how
        many runs, whether it passed, whether you revealed the answer, and the resulting score.
      </p>

      <h2>What is stored</h2>
      <ul>
        <li>
          <strong>Your account:</strong> email, an optional name, and a hashed password. Held by gazar.dev,
          under <a href="https://gazar.dev/privacy">its privacy policy</a>.
        </li>
        <li>
          <strong>Your practice:</strong> one row per attempt, and one schedule row per problem.
        </li>
        <li>
          <strong>Your handle:</strong> only if you choose one.
        </li>
      </ul>

      <h2>What is public</h2>
      <p>
        Nothing, unless you switch it on. The <Link to="/scoreboard">scoreboard</Link> is opt-in and lists
        the handle you chose plus your scores. Your name and email are never published there, and turning
        the listing off, or clearing your handle, removes you immediately.
      </p>

      <h2>Analytics and errors</h2>
      <p>
        Page views and drill events (which problem, which pattern, the grade) go to a self-hosted OpenPanel
        instance, and crashes go to a self-hosted GlitchTip. Both run on the same infrastructure as the
        rest of gazar.dev. Neither is Google, neither is an ad network, and no data is sold or shared with
        one. Your solutions are not included in either.
      </p>

      <h2>Cookies</h2>
      <p>
        One: the session cookie that keeps you signed in. It is httpOnly, scoped to gazar.dev, and there is
        no advertising or tracking cookie to consent to.
      </p>

      <h2>Deleting everything</h2>
      <p>
        Email <a href="mailto:me@gazar.dev">me@gazar.dev</a> and the account, its attempts and its schedule
        are deleted. There is no dark-pattern retention window.
      </p>
    </article>
  );
}
